import {
    useMutation,
    useQuery,
    useQueryClient,
    UseQueryResult,
} from '@tanstack/react-query';

import {
    CRMCustomersApi,
    CRMCustomersBusinessReportsApi,
    CrmCustomersBusinessReportsGetCreationReportsRequest,
    CrmCustomersBusinessReportsGetProductReportsByAggregationFromDateToDateStatesRequest,
    CrmCustomersBusinessReportsGetStatusReportsRequest,
    CrmCustomersBusinessReportsGetTenantReportsRequest,
    CrmCustomersBusinessReportsGetVendorReportsByAggregationFromDateToDateStatesRequest,
    CrmCustomersGetStatsCustomersRequest,
    CrmCustomersGetStatsRequest,
    CRMCustomersHealthApi,
    CrmCustomersHealthGetCustomerSiteHealthRequest,
    CrmCustomersUploadCsvCustomersRequest,
    CrmCustomersUploadCsvForCustomerRequest,
    CRMInstallationsHeatPumpApi,
    CrmInstallationsHeatPumpDeleteHeatPumpDeviceRequest,
    CrmInstallationsMeterAddMeterDeviceRequest,
    CRMInstallationsMeterApi,
    CrmInstallationsMeterDeleteMeterDeviceRequest,
    CrmInstallationsMeterUpdateGridXMeterDeviceRequest,
    CrmInstallationsMeterUpdateMeterDeviceRequest,
    CRMInstallationsPVBatteryApi,
    CrmInstallationsPvBatteryDeleteSmartEnergyDeviceRequest,
    CRMInstallationsWallBoxApi,
    CrmInstallationsWallBoxDeleteWallboxSmartEnergyDeviceRequest,
    CustomerAddModel,
    CustomerResponseModel,
    CustomersOrderByEnum,
    CustomersOrderDirectionEnum,
    CustomerStats,
    CustomerUserResponseModel,
    InstallationsGatewayApi,
    InstallationsGatewayDeleteGatewayDeviceRequest,
    InstallationStatsModel,
    IoTVendorFilterModel,
    MeterInstallationRequestModel,
    SentInvitationResponseModel,
    SettingsApi,
} from '@swagger-http';

import { createRequestConfiguration } from '@tools/utils/authentication';

import { convertCharsetToIso8859 } from '@tools/utils';

interface BusinessReportProps
    extends CrmCustomersBusinessReportsGetCreationReportsRequest {
    isCumulative: boolean;
}

interface CustomerArgs {
    customerId?: string;
    page?: number;
    limit?: number;
    order?: {
        by: CustomersOrderByEnum;
        direction: CustomersOrderDirectionEnum;
    };
    filter?: any;
    searchValue?: string;
    enabled?: boolean;
    csvExport?: boolean;
}

export const useCustomersGetInstallationsStatsQuery = (enabled: boolean) =>
    useQuery<InstallationStatsModel, Error>({
        queryKey: ['customersInstallationsStats'],
        queryFn: async () =>
            await new CRMCustomersApi(
                createRequestConfiguration(),
            ).crmCustomersGetInstallationsStats(),
        enabled,
    });

interface CustomerGetStatsRequest extends CrmCustomersGetStatsCustomersRequest {
    customerId?: string;
}

export const useCustomerGetInstallationsAccountsQuery = ({
    filters,
    customerId,
}: CustomerGetStatsRequest) =>
    useQuery({
        queryKey: [
            'customersInstallationsAccounts',
            { filters: filters ?? { installation: [] } },
        ],
        queryFn: async () =>
            await new CRMCustomersApi(
                createRequestConfiguration(),
            ).crmCustomersGetStatsCustomers({
                filters,
            }),
        enabled: !customerId,
    });

export const useCustomersStatsChildren = () =>
    useQuery({
        queryKey: ['customersStatsChildren'],
        queryFn: async () =>
            await new CRMCustomersApi(
                createRequestConfiguration(),
            ).crmCustomersGetStatsCustomers(),
    });

export const useCustomersGetCustomerStatsChildren = ({
    filters,
    customerId,
}: CustomerGetStatsRequest) =>
    useQuery({
        queryKey: [
            'customersGetCustomerStatsChildren',
            { filters: filters ?? { installation: [] } },
        ],
        queryFn: async () =>
            await new CRMCustomersApi(
                createRequestConfiguration(),
            ).crmCustomersGetCustomerStatsChildren({
                filters,
                id: customerId ?? '',
            }),

        enabled: !!customerId,
    });

interface CustomersGetStatsArgs extends CrmCustomersGetStatsRequest {
    enabled?: boolean;
}

export const useCustomersGetStatsQuery = ({
    filters,
    enabled,
}: CustomersGetStatsArgs) =>
    useQuery<CustomerStats, Error>({
        queryKey: [
            'customersStats',
            { filters: filters ?? { installation: [] } },
        ],
        queryFn: async () =>
            await new CRMCustomersApi(
                createRequestConfiguration(),
            ).crmCustomersGetStats({
                filters: filters ?? undefined,
            }),
        enabled: enabled && !!filters?.installation?.length,
    });

export const useCustomersBusinessReportQuery = ({
    aggregation,
    fromDate,
    toDate,
    isCumulative,
    filters,
}: BusinessReportProps) =>
    useQuery({
        queryKey: [
            'customersBusinessReports',
            { aggregation, fromDate, toDate, isCumulative, filters },
        ],
        enabled: !!filters?.installation?.length,
        queryFn: async () => {
            const requestParams = {
                aggregation,
                fromDate,
                toDate,
                filters,
            };
            const crmCustomersBusinessReportsApi =
                new CRMCustomersBusinessReportsApi(
                    createRequestConfiguration(),
                );
            return isCumulative
                ? await crmCustomersBusinessReportsApi.crmCustomersBusinessReportsGetCreationReportsIncremental(
                      requestParams,
                  )
                : await crmCustomersBusinessReportsApi.crmCustomersBusinessReportsGetCreationReports(
                      requestParams,
                  );
        },
    });

export const useCustomersTenantReports = ({
    aggregation,
    fromDate,
    toDate,
}: CrmCustomersBusinessReportsGetTenantReportsRequest) =>
    useQuery({
        queryKey: [
            'customersTenantsReports',
            { aggregation, fromDate, toDate },
        ],
        queryFn: async () => {
            const requestParams = {
                aggregation,
                fromDate,
                toDate,
            };
            const crmCustomersBusinessReportsApi =
                new CRMCustomersBusinessReportsApi(
                    createRequestConfiguration(),
                );
            return await crmCustomersBusinessReportsApi.crmCustomersBusinessReportsGetTenantReports(
                requestParams,
            );
        },
    });

export const useDevicesConfigurations = () =>
    useQuery({
        queryKey: ['devicesConfigurationsReport'],
        queryFn: async () => {
            const crmCustomersBusinessReportsApi =
                new CRMCustomersBusinessReportsApi(
                    createRequestConfiguration(),
                );
            return await crmCustomersBusinessReportsApi.crmCustomersBusinessReportsGetManagerReportDevicesConfigurations();
        },
    });

export const useUsersActivities = () =>
    useQuery({
        queryKey: ['usersActivitiesReport'],
        queryFn: async () => {
            const crmCustomersBusinessReportsApi =
                new CRMCustomersBusinessReportsApi(
                    createRequestConfiguration(),
                );
            return await crmCustomersBusinessReportsApi.crmCustomersBusinessReportsGetManagerReportUsersActivity();
        },
    });

export const useCustomersProductReportsBasedOnStates = ({
    aggregation,
    fromDate,
    toDate,
    states,
}: CrmCustomersBusinessReportsGetProductReportsByAggregationFromDateToDateStatesRequest) =>
    useQuery({
        queryKey: [
            'customersProductsReportsBasedOnStates',
            { aggregation, fromDate, toDate, states },
        ],
        queryFn: async () => {
            const requestParams = {
                aggregation,
                fromDate,
                toDate,
                states,
            };
            const crmCustomersBusinessReportsApi =
                new CRMCustomersBusinessReportsApi(
                    createRequestConfiguration(),
                );
            return await crmCustomersBusinessReportsApi.crmCustomersBusinessReportsGetProductReportsByAggregationFromDateToDateStates(
                requestParams,
            );
        },
    });

export const useGetVendors = (
    filter?: IoTVendorFilterModel,
    enabled?: boolean,
) =>
    useQuery({
        queryKey: ['customersVendors'],
        queryFn: async () => {
            const settingsApi = new SettingsApi(createRequestConfiguration());
            return await settingsApi.settingsGetVendors(
                filter ? { filter } : undefined,
            );
        },
        enabled,
    });

export const useCustomersVendorReportsBasedOnStates = ({
    aggregation,
    fromDate,
    toDate,
    states,
}: CrmCustomersBusinessReportsGetVendorReportsByAggregationFromDateToDateStatesRequest) =>
    useQuery({
        queryKey: [
            'customersVendorsReportsBasedOnStates',
            { aggregation, fromDate, toDate, states },
        ],
        queryFn: async () => {
            const requestParams = {
                aggregation,
                fromDate,
                toDate,
                states,
            };
            const crmCustomersBusinessReportsApi =
                new CRMCustomersBusinessReportsApi(
                    createRequestConfiguration(),
                );
            return await crmCustomersBusinessReportsApi.crmCustomersBusinessReportsGetVendorReportsByAggregationFromDateToDateStates(
                requestParams,
            );
        },
    });

export const useCustomersStatusReports = ({
    aggregation,
    fromDate,
    toDate,
}: CrmCustomersBusinessReportsGetStatusReportsRequest) =>
    useQuery({
        queryKey: [
            'customersStatusesReports',
            { aggregation, fromDate, toDate },
        ],
        queryFn: async () => {
            const requestParams = {
                aggregation,
                fromDate,
                toDate,
            };
            const crmCustomersBusinessReportsApi =
                new CRMCustomersBusinessReportsApi(
                    createRequestConfiguration(),
                );
            return await crmCustomersBusinessReportsApi.crmCustomersBusinessReportsGetStatusReports(
                requestParams,
            );
        },
    });

export const useCustomersHomeComfortReports = () =>
    useQuery({
        queryKey: ['customersHomeComfortReports'],
        queryFn: async () => {
            const crmCustomersBusinessReportsApi =
                new CRMCustomersBusinessReportsApi(
                    createRequestConfiguration(),
                );
            return await crmCustomersBusinessReportsApi.crmCustomersBusinessReportsGetHomeComfortBonusReports();
        },
    });

export const useCustomersHomeComfortGetReport = (fileName: string) =>
    useQuery({
        queryKey: ['customersHomeComfortReport', fileName],
        queryFn: async () => {
            const crmCustomersBusinessReportsApi =
                new CRMCustomersBusinessReportsApi(
                    createRequestConfiguration(),
                );
            return await crmCustomersBusinessReportsApi.crmCustomersBusinessReportsDownloadHomeComfortBonusReport(
                { fileName },
            );
        },
        enabled: !!fileName,
    });

export const queryCrmCustomers = async ({
    page,
    limit,
    order,
    filter,
    searchValue,
    csvExport,
}: CustomerArgs) => {
    const response = await new CRMCustomersApi(
        createRequestConfiguration(),
    ).crmCustomersGetCustomers({
        page: page ?? 0,
        limit: limit ?? -1,
        order,
        filter,
        searchValue,
        csvExport,
    });

    return response;
};
export const useCrmCustomersGetCustomersQuery = ({
    page,
    limit,
    order,
    filter,
    searchValue,
    csvExport,
    enabled,
}: CustomerArgs) =>
    useQuery({
        queryKey: [
            'getCrmCustomers',
            page,
            limit,
            order,
            filter,
            searchValue,
            csvExport,
        ],
        enabled,
        queryFn: async () => {
            const response = await new CRMCustomersApi(
                createRequestConfiguration(),
            ).crmCustomersGetCustomers({
                page: page ?? 0,
                limit: limit ?? 0,
                order,
                filter,
                searchValue,
                csvExport,
            });

            return response;
        },
    });

export const useCrmCustomersGetCmsCustomers = () =>
    useQuery({
        queryKey: ['getCMSCustomers'],
        queryFn: async () => {
            const response = await new CRMCustomersApi(
                createRequestConfiguration(),
            ).crmCustomersGetCmsCustomers();

            return response;
        },
    });

export const useCrmCustomersGetSystemOwners = () =>
    useQuery({
        queryKey: ['getCMSSystemOwners'],
        queryFn: async () => {
            const response = await new CRMCustomersApi(
                createRequestConfiguration(),
            ).crmCustomersGetCmsCustomers({ systemOwners: true });

            return response;
        },
    });

export const useCrmCustomersHealthGetSitesHealthMutation = (retry?: boolean) =>
    useMutation({
        mutationKey: ['getCrmCustomersHealth'],
        retry,
        mutationFn: async (customerIds: string[]) => {
            const payload = {
                sitesHealthRequestBody: {
                    customerIds: [...new Set(customerIds)],
                },
            };
            const response = new CRMCustomersHealthApi(
                createRequestConfiguration(),
                //@ts-ignore
                //TODO: Fix Swagger interface from Set to string[]
            ).crmCustomersHealthGetSitesHealth(payload);
            return response;
        },
    });

export const useSingleCustomerDetails = (
    customerId: string,
    enabled?: boolean,
): UseQueryResult<CustomerResponseModel> =>
    useQuery({
        queryKey: ['getCrmCustomer', customerId],
        enabled,
        queryFn: async () =>
            await new CRMCustomersApi(
                createRequestConfiguration(),
            ).crmCustomersGetCustomer({ id: customerId }),
    });

export const useCrmCustomersHealthGetCustomerSiteHealthMutation = (
    retry?: boolean,
) =>
    useMutation({
        mutationKey: ['getCrmSingleCustomerHealth'],
        retry,
        mutationFn: async (customerId: string) => {
            const response = await new CRMCustomersHealthApi(
                createRequestConfiguration(),
            ).crmCustomersHealthGetCustomerSiteHealth({
                customerId,
            } as CrmCustomersHealthGetCustomerSiteHealthRequest);

            return response;
        },
    });

export const useCustomerUsers = (
    id: string,
): UseQueryResult<CustomerUserResponseModel[]> =>
    useQuery({
        queryKey: ['getCrmUsersCustomer', id],
        queryFn: async () =>
            await new CRMCustomersApi(
                createRequestConfiguration(),
            ).crmCustomersGetCustomerUsers({ id }),
    });

export const useCustomerHistoryOfSentUserInvitations = (
    id: string,
): UseQueryResult<SentInvitationResponseModel[]> =>
    useQuery({
        queryKey: ['getHistoryOfSentUserInvitations', id],
        queryFn: async () =>
            await new CRMCustomersApi(
                createRequestConfiguration(),
            ).crmCustomersGetSendAccountUserInvitations({ id }),
    });

export const useCreateCustomer = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['useCreateCustomer'],
        mutationFn: async (payload: CustomerAddModel) =>
            new CRMCustomersApi(
                createRequestConfiguration(),
            ).crmCustomersAddAccountUser({ customerAddModel: payload }),
        onSuccess: async () => {
            queryClient.invalidateQueries({ queryKey: ['getCrmCustomers'] });
        },
    });
};

export const useCustomerSites = (customerId: string, enabled?: boolean) =>
    useQuery({
        queryKey: ['crmCustomersCustomerGetSites', customerId],
        enabled,
        queryFn: async () =>
            await new CRMCustomersApi(
                createRequestConfiguration(),
            ).crmCustomersCustomerGetSites({ id: customerId }),
    });

export const useGetSingleCustomerAccessToken = (
    customerId: string,
    enabled?: boolean,
) =>
    useQuery({
        queryKey: ['getSingleCustomerAccessToken', customerId],
        enabled,
        queryFn: async () =>
            await new CRMCustomersApi(
                createRequestConfiguration(),
            ).crmCustomersGetAuthTokens({ id: customerId }),
    });

export const useGetSingleCustomerIdpProvider = (customerId: string) =>
    useQuery({
        queryKey: ['getSingleCustomerIdpProvider', { customerId }],
        queryFn: async () =>
            await new CRMCustomersApi(
                createRequestConfiguration(),
            ).crmCustomersCustomerGetIdpProvider({ id: customerId }),
    });

export const useAddInstallationsMeterDeviceMutation = (customerId: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['crmInstallationsMeterAddMeterDevice', customerId],
        mutationFn: async (smartMeterData: MeterInstallationRequestModel) => {
            const data: CrmInstallationsMeterAddMeterDeviceRequest = {
                id: customerId,
                meterInstallationRequestModel: smartMeterData,
            };

            return await new CRMInstallationsMeterApi(
                createRequestConfiguration(),
            ).crmInstallationsMeterAddMeterDevice(data);
        },
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['getSmartMeterInstallationStatus', customerId],
            });
        },
    });
};

export const useCustomersCompareCustomerData = (
    customerId: string,
    configId: string,
    date?: string,
) =>
    useQuery({
        queryKey: ['customersCompareCustomerData', customerId, date],
        queryFn: async () =>
            await new CRMCustomersApi(
                createRequestConfiguration(),
            ).crmCustomersCompareCustomerData({
                customerId: customerId,
                configId: configId,
                date: date,
            }),
    });

export const useUpdateSmartMeterInstallation = (
    customerId: string,
    retry?: boolean,
) => {
    const queryClient = useQueryClient();

    return useMutation({
        retry,
        mutationKey: ['updateSmartMeterInstallation', customerId],
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['getSmartMeterInstallationStatus', customerId],
            });
        },
        mutationFn: async ({
            smartMeterData,
            configurationId,
        }: {
            smartMeterData: MeterInstallationRequestModel;
            configurationId: string;
        }) => {
            const data: CrmInstallationsMeterUpdateMeterDeviceRequest = {
                id: customerId,
                configurationId,
                meterInstallationRequestModel: smartMeterData,
            };

            const response = await new CRMInstallationsMeterApi(
                createRequestConfiguration(),
            ).crmInstallationsMeterUpdateMeterDevice(data);

            return response;
        },
    });
};

export const useDeleteSmartMeterInstallation = (
    customerId: string,
    retry?: boolean,
) => {
    const queryClient = useQueryClient();

    return useMutation({
        retry,
        mutationKey: ['deleteSmartMeterInstallation', customerId],
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['getSmartMeterInstallationStatus', customerId],
            });
        },
        mutationFn: async ({
            configurationId,
        }: {
            configurationId: string;
        }) => {
            const data: CrmInstallationsMeterDeleteMeterDeviceRequest = {
                id: customerId,
                configurationId,
            };

            const response = await new CRMInstallationsMeterApi(
                createRequestConfiguration(),
            ).crmInstallationsMeterDeleteMeterDevice(data);

            return response;
        },
    });
};

export const useDeletePvBatteryInstallation = (
    customerId: string,
    retry?: boolean,
) => {
    const queryClient = useQueryClient();

    return useMutation({
        retry,
        mutationKey: ['deletePvBatteryInstallation', customerId],
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['getPvBatteryInstallationStatus', customerId],
            });
        },
        mutationFn: async ({
            configurationId,
        }: {
            configurationId: string;
        }) => {
            const data: CrmInstallationsPvBatteryDeleteSmartEnergyDeviceRequest =
                {
                    id: customerId,
                    configurationId,
                };

            const response = await new CRMInstallationsPVBatteryApi(
                createRequestConfiguration(),
            ).crmInstallationsPvBatteryDeleteSmartEnergyDevice(data);

            return response;
        },
    });
};

export const useDeleteWallboxInstallation = (
    customerId: string,
    retry?: boolean,
) => {
    const queryClient = useQueryClient();

    return useMutation({
        retry,
        mutationKey: ['deleteWallboxInstallation', customerId],
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['getWallboxInstallationStatus', customerId],
            });
        },
        mutationFn: async ({
            configurationId,
        }: {
            configurationId: string;
        }) => {
            const data: CrmInstallationsWallBoxDeleteWallboxSmartEnergyDeviceRequest =
                {
                    id: customerId,
                    deviceId: configurationId,
                };

            const response = await new CRMInstallationsWallBoxApi(
                createRequestConfiguration(),
            ).crmInstallationsWallBoxDeleteWallboxSmartEnergyDevice(data);

            return response;
        },
    });
};

export const useDeleteGatewayInstallation = (
    customerId: string,
    retry?: boolean,
) => {
    const queryClient = useQueryClient();

    return useMutation({
        retry,
        mutationKey: ['deleteGatewayDevice', customerId],
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['getGatewayDevices', customerId],
            });
        },
        mutationFn: async ({
            configurationId,
        }: {
            configurationId: string;
        }) => {
            const data: InstallationsGatewayDeleteGatewayDeviceRequest = {
                id: customerId,
                installationId: configurationId,
            };

            const response = await new InstallationsGatewayApi(
                createRequestConfiguration(),
            ).installationsGatewayDeleteGatewayDevice(data);

            return response;
        },
    });
};

export const useDeleteHeatPumpInstallation = (
    customerId: string,
    retry?: boolean,
) => {
    const queryClient = useQueryClient();

    return useMutation({
        retry,
        mutationKey: ['deleteHeatPumpDevice', customerId],
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['getHeatPumpInstallationStatus', customerId],
                refetchType: 'all',
            });
        },
        mutationFn: async ({
            configurationId,
        }: {
            configurationId: string;
        }) => {
            const data: CrmInstallationsHeatPumpDeleteHeatPumpDeviceRequest = {
                id: customerId,
                deviceId: configurationId,
            };
            const response = await new CRMInstallationsHeatPumpApi(
                createRequestConfiguration(),
            ).crmInstallationsHeatPumpDeleteHeatPumpDevice(data);

            return response;
        },
    });
};

export const getCustomerCustomers = async ({
    customerId,
    page,
    limit,
    order,
    filter,
    searchValue,
    csvExport,
}: CustomerArgs) =>
    await new CRMCustomersApi(
        createRequestConfiguration(),
    ).crmCustomersGetCustomerCustomers({
        id: customerId ?? '',
        page,
        limit,
        order,
        filter,
        searchValue,
        csvExport,
    });

export const useGetCustomerCustomers = ({
    customerId,
    page,
    limit,
    order,
    filter,
    searchValue,
    enabled,
    csvExport,
}: CustomerArgs) =>
    useQuery({
        queryKey: [
            'getCustomerCustomers',
            customerId,
            page,
            limit,
            order,
            filter,
            searchValue,
            csvExport,
        ],

        enabled: enabled && !!customerId,
        queryFn: async () =>
            await new CRMCustomersApi(
                createRequestConfiguration(),
            ).crmCustomersGetCustomerCustomers({
                id: customerId ?? '',
                page,
                limit,
                order,
                filter,
                searchValue,
                csvExport,
            }),
    });

export const useCustomersGetCustomerInstallationsStats = (customerId: string) =>
    useQuery<InstallationStatsModel, Error>({
        queryKey: ['customersInstallationsStats', customerId],
        queryFn: async () =>
            await new CRMCustomersApi(
                createRequestConfiguration(),
            ).crmCustomersGetCustomerInstallationsStats({
                id: customerId,
            }),
        enabled: !!customerId,
    });

export const useGetCustomerStats = (customerId?: string, filters?: object) =>
    useQuery({
        queryKey: [
            'getCustomerStats',
            customerId,
            { filters: filters ?? { installation: [] } },
        ],
        queryFn: async () =>
            await new CRMCustomersApi(
                createRequestConfiguration(),
            ).crmCustomersGetCustomerStats({
                id: customerId ?? '',
                filters: filters,
            }),
        //@ts-ignore
        enabled: !!customerId && !!filters?.installation?.length,
    });

export const useUploadCreateCustomersCsvMutation = (retry?: boolean) =>
    useMutation({
        retry,
        mutationKey: ['uploadCreateCustomersCsv'],
        mutationFn: async (payload: { id?: string; csvData?: Blob }) => {
            if (payload?.csvData) {
                const csvData = await convertCharsetToIso8859(payload?.csvData);
                const originalFilename =
                    (payload.csvData as File).name || 'file.csv';

                const formData = new FormData();
                formData.append('csvData', csvData, originalFilename);
                payload = {
                    id: payload.id,
                    csvData: formData.get('csvData') as Blob,
                };
            }

            const crmApi = new CRMCustomersApi(createRequestConfiguration());
            return payload?.id
                ? crmApi.crmCustomersUploadCsvForCustomer(
                      payload as CrmCustomersUploadCsvForCustomerRequest,
                  )
                : crmApi.crmCustomersUploadCsvCustomers(
                      payload as CrmCustomersUploadCsvCustomersRequest,
                  );
        },
    });

export const useGetEmailCampaignCustomerStatus = (customerId: string) =>
    useQuery({
        queryKey: ['getEmailCampaignCustomerStatus', customerId],
        queryFn: async () =>
            await new CRMCustomersApi(
                createRequestConfiguration(),
            ).crmCustomersGetSendUserEmailCampaigns({ id: customerId }),
        enabled: !!customerId,
    });

export const getSolarVendors = () => {
    const settingsApi = new SettingsApi(createRequestConfiguration());
    return settingsApi.settingsGetVendors();
};

export const useUpdateSmartMeterKind = (customerId: string) => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: ['updateSmartMeterKind', customerId],
        mutationFn: async (
            payload: CrmInstallationsMeterUpdateGridXMeterDeviceRequest,
        ) =>
            new CRMInstallationsMeterApi(
                createRequestConfiguration(),
            ).crmInstallationsMeterUpdateGridXMeterDevice(payload),
        onSuccess: () => {
            queryClient.invalidateQueries({
                queryKey: ['getSmartMeterInstallationStatus', customerId],
            });
        },
    });
};

export const useCustomersGetBMWReport = () =>
    useQuery({
        queryKey: ['customersBMWReport'],
        queryFn: async () =>
            await new CRMCustomersBusinessReportsApi(
                createRequestConfiguration(),
            ).crmCustomersBusinessReportsGetEonHomeAndDriveBmwAndMiniReport(),
        enabled: false,
    });
